import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../../lib/api';
import { safeConfirm } from '../../lib/confirm';
import { Briefcase, Plus, Search, ShieldCheck } from 'lucide-react';
import { HRForm, EMPTY } from './hr/form';
import { HRDetail } from './hr/HRDetail';

export default function AdminHR() {
  const [employees, setEmployees] = useState([]);
  const [meta, setMeta] = useState(null);
  const [admins, setAdmins] = useState([]);
  const [banks, setBanks] = useState([]);
  const [editing, setEditing] = useState(null);
  const [detail, setDetail] = useState(null);
  const [q, setQ] = useState('');

  const load = () => api.get('/hr/employees').then(r => setEmployees(r.data));
  const loadBanks = () => api.get('/hr/banks').then(r => setBanks(r.data?.banks || [])).catch(() => {});
  useEffect(() => {
    load();
    loadBanks();
    api.get('/hr/meta').then(r => setMeta(r.data));
    api.get('/users').then(r => setAdmins((r.data || []).filter(u => u.role === 'admin'))).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    const s = q.toLowerCase();
    return employees.filter(e => !s || `${e.first_name} ${e.last_name} ${e.email} ${e.professional_title}`.toLowerCase().includes(s));
  }, [employees, q]);

  const remove = async (e) => {
    if (!(await safeConfirm(`Delete the HR record for ${e.first_name} ${e.last_name}? This cannot be undone.`))) return;
    await api.delete(`/hr/employees/${e.employee_id}`);
    setDetail(null); load();
  };

  return (
    <div className="space-y-6" data-testid="admin-hr-page">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold flex items-center gap-2"><Briefcase className="w-7 h-7 text-brand"/> HR — Hiring & staff records</h1>
          <p className="text-sm text-fg-muted mt-1 flex items-center gap-1.5">
            <ShieldCheck className="w-4 h-4 text-emerald-600"/> Admin-only. HR records are never visible to clinicians or reception.
          </p>
        </div>
        <button className="btn-primary inline-flex items-center gap-2" onClick={() => setEditing({ ...EMPTY })} data-testid="hr-new-hire-btn">
          <Plus className="w-4 h-4"/> New hire
        </button>
      </div>

      <div className="relative max-w-md">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-fg-muted"/>
        <input className="input !pl-9" placeholder="Search staff…" value={q} onChange={e => setQ(e.target.value)} data-testid="hr-search"/>
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {filtered.map(e => (
          <div key={e.employee_id} className="bento-card cursor-pointer hover:border-brand/40 transition-colors" onClick={() => setDetail(e)} data-testid={`hr-card-${e.employee_id}`}>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-[#F2EBDB] overflow-hidden flex items-center justify-center shrink-0">
                {e.photo_url ? <img src={e.photo_url} alt="" className="w-full h-full object-cover"/> : <Briefcase className="w-5 h-5 text-brand"/>}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-heading font-semibold truncate">{e.first_name} {e.last_name}</div>
                <div className="text-xs text-fg-muted truncate">{e.professional_title || e.staff_type} · {(e.hire_type || '').replace(/_/g, ' ')}</div>
              </div>
              {e.date_separated
                ? <span className="badge-neutral text-[10px]">left {e.date_separated}</span>
                : <span className="badge-success text-[10px]">active</span>}
            </div>
          </div>
        ))}
        {filtered.length === 0 && (
          <div className="text-sm text-fg-muted md:col-span-3 py-10 text-center">No HR records yet — click <strong>New hire</strong> to create the first hiring package.</div>
        )}
      </div>

      {editing && <HRForm initial={editing} meta={meta} admins={admins} banks={banks}
                          onClose={() => setEditing(null)}
                          onSaved={() => { setEditing(null); load(); loadBanks(); }}/>}
      {detail && <HRDetail emp={detail} onClose={() => setDetail(null)} onEdit={() => { setEditing(detail); setDetail(null); }}
                           onDelete={() => remove(detail)} onChanged={load}/>}

      <div className="surface border border-soft rounded-lg p-4 text-xs text-fg-muted space-y-1.5" data-testid="hr-compliance-panel">
        <div className="font-semibold text-fg flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-emerald-600"/> HR, payroll & banking compliance</div>
        <p>• <strong>GDPR (Art. 6(1)(b) & 88)</strong> — banking details and HR records are processed for employment-contract performance only, visible to admins alone, and never shared with clinicians, reception or patients.</p>
        <p>• <strong>Records retention</strong> — keep employment & payroll documentation for the statutory period (Poland: 10 years for post-2019 hires; UK: 6 years; check your jurisdiction). Do not delete HR records of former staff before that period ends — use the separation date instead.</p>
        <p>• <strong>Pay stubs</strong> — most jurisdictions require an itemized written/electronic pay statement each pay period. The pay stub email and printout satisfy the itemization; keep copies in the personnel file.</p>
        <p>• <strong>Payment of wages</strong> — salaries in Poland must be paid at least monthly, by the 10th of the following month, by bank transfer unless the employee requests otherwise in writing. Sick pay is 80% of the base (Kodeks pracy art. 92); vacation leave is paid at 100%.</p>
        <p>• <strong>Certificates</strong> — on separation, issue the certificate of employment where required (e.g. świadectwo pracy within 7 days in Poland). Tax and payroll figures shown here are estimates — confirm statutory filings with your accountant.</p>
      </div>
    </div>
  );
}
